import styled from 'styled-components';
import { useRestaurantOrders, useRestaurantPreorders } from '../../../apis/orders-api';
import { useOrderFilters } from '../hooks/useOrderFilters';
import { useOrdersDateParam } from '../hooks/useOrdersDateParam';
import { OrdersFilterBar } from '../OrdersList/OrdersFilterBar';
import { OrdersList } from '../OrdersList/OrdersList';
import { OrdersDateStrip } from './OrdersDateStrip';

/**
 * One restaurant's orders for one day, with the strip that picks the day.
 *
 * Both restaurant entry points are this component plus their own heading: the `/:restaurant/orders/`
 * route and the Orders tab on the restaurant page. Keeping it here rather than in the tab is what
 * stops the route view from having to impersonate a tab host to reuse it.
 */
export const RestaurantOrdersByDay: React.FC<{ shopExternalId: string }> = ({ shopExternalId }) => {
  // The day is held in the query string, so it survives a reload, can be linked to — the dashboard's
  // preorder card points straight at a day — and the back gesture leaves by the way it came in.
  const { date, setDate } = useOrdersDateParam();
  // `keepPreviousData` so changing the day swaps the rows instead of unmounting the list: this view
  // keeps its filters and its open details panel across a date change, and there is no blank frame
  // between the two days. And so `isFetching`, not `isLoading`: holding the previous day's rows up
  // holds `isLoading` false, so a date change would otherwise pass unannounced.
  const { data: orders, isFetching } = useRestaurantOrders(shopExternalId, date, { keepPreviousData: true });
  const { data: preorders } = useRestaurantPreorders(shopExternalId);
  // Held here rather than inside the list, so the filter bar can sit beside the day picker.
  const filters = useOrderFilters();

  return (
    <Layout>
      {/* No date heading: the strip highlights the selected day, so a line repeating it is noise. */}
      <Controls>
        <OrdersDateStrip date={date} onDateChange={setDate} preorders={preorders} />
        <OrdersFilterBar
          orderFilter={filters.orderFilter}
          onOrderFilterChange={filters.setOrderFilter}
          sortKey={filters.sortKey}
          onSortKeyChange={filters.setSortKey}
        />
      </Controls>
      <OrdersList ordersFromQuery={orders ?? []} isLoading={isFetching} filters={filters} />
    </Layout>
  );
};

/**
 * Owns the spacing between the controls and the list.
 *
 * Returning these as a fragment put both of them straight into the page shell's flex, so the shell's
 * 20px gap landed between a component's own two halves — and stacked with the row's margin, leaving
 * the list further from its own filter than the filter was from the page title. One wrapper, and the
 * shell is back to spacing page blocks only.
 */
const Layout = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
`;

/**
 * The day picker and the filter on one line, with the list beneath them.
 *
 * Only from md up: below that the strip needs the full width for its sideways scroll, so the filter
 * drops under it. Both children carry a bottom margin for when they stand alone, which this row
 * takes over — otherwise the two would end at different heights.
 */
const Controls = styled.div`
  display: flex;
  flex-direction: column;
  /* Stacked on a phone, so this is the space between the day strip and the filter beneath it. The
     strip's own bottom margin is zeroed below, which is what this replaces. */
  gap: ${({ theme }) => theme.spacing(2)};

  > *:first-child,
  > *:last-child {
    margin-bottom: 0;
  }

  ${({ theme }) => theme.breakpoints.up('md')} {
    flex-direction: row;
    /* Bottom, not centre: the strip is ~76px of day buttons and the filter is a ~30px pill, so
       centring left it floating mid-row. Aligned to the bottom it sits on the same line the list
       starts from. */
    align-items: flex-end;
    gap: ${({ theme }) => theme.spacing(2)};

    /* The strip takes the room left over; the filter shrinks to its pills. */
    > *:first-child {
      flex: 1;
      min-width: 0;
    }
  }
`;
